import { Advert, Category } from '../models';
import { serverSlotsToClientSlots } from '../constants/advertSlot';
import { staticAdvertData, advertSize } from './utils/advertStaticData';
import { client as apiClient } from '../api/client';

export interface ApiAdvert extends Partial<Advert> {
  image: string;
  name: string;
  link: string;
  starting_at: Date;
  ending_at: Date;
}

// Type definition for what the api response looks like
type GroupedApiAdverts = { [slotName: string]: Array<ApiAdvert> };
type GroupedAdverts = { [slotName: string]: Array<Advert> };

export class AdvertRepository {
  public async fetchAll(): Promise<GroupedAdverts> {
    return this.fetchAdvertsData('adverts?groupBy=slot');
  }

  public async fetchForVideoCategory(
    videoCategory: Category | undefined
  ): Promise<{ [slotName: string]: Array<Advert> }> {
    return this.fetchAdvertsData(
      `adverts?groupBy=slot&category=${videoCategory ? videoCategory.id : ''}`
    );
  }

  public buildAdverts(
    data: GroupedApiAdverts
  ): { [slotName: string]: Array<Advert> } {
    const adverts: { [slotName: string]: Array<Advert> } = {};

    // Use `advertSize` keys to loop over server slots, making it easy to fill blanks
    Object.keys(advertSize).forEach(serverSlotName => {
      // Default to static/default ad data
      let advertData = staticAdvertData[serverSlotName];

      // Use a randomly selected server ad if we got some for this slot
      if (data[serverSlotName] && data[serverSlotName].length > 0) {
        advertData =
          data[serverSlotName][
            Math.floor(Math.random() * data[serverSlotName].length)
          ];
      }

      // Always get responsive size limits locally, since server never sends them
      advertData.minWidth = staticAdvertData[serverSlotName].minWidth;
      advertData.maxWidth = staticAdvertData[serverSlotName].maxWidth;

      const clientSlotName = serverSlotsToClientSlots[serverSlotName];

      adverts[clientSlotName] = adverts[clientSlotName] || [];

      adverts[clientSlotName].push(
        new Advert(
          advertData.id as number,
          (advertData.altText = advertData.name),
          advertData.slot as string,
          (advertData.href = advertData.link),
          (advertData.imageUrl = advertData.image),
          advertData.active as boolean,
          (advertData.minWidth = advertData.minWidth),
          (advertData.maxWidth = advertData.maxWidth),
          (advertData.startingAt = advertData.starting_at),
          (advertData.endingAt = advertData.ending_at)
        )
      );
    });

    return adverts;
  }

  private async fetchAdvertsData(url: string): Promise<GroupedAdverts> {
    let adverts: GroupedAdverts;

    try {
      adverts = await apiClient.getRaw<GroupedAdverts, GroupedApiAdverts>(
        url,
        this.buildAdverts
      );
    } catch (ex) {
      // Since adverts are non-critical to the application, swallow any failure
      // from the API and just return the static built-in adverts instead

      // tslint:disable-next-line: no-console
      console.error(`Failed to fetch advert data from API (${url})`);

      adverts = this.buildAdverts({});
    }

    return adverts;
  }
}

let RepositoryClass: ImportedModule<Constructable<AdvertRepository>>;
if (process.env.REACT_APP_NO_BACKEND === 'true') {
  RepositoryClass = require('./AdvertMock');
} else {
  RepositoryClass = { default: AdvertRepository };
}

let repository: AdvertRepository;
export default function getRepository() {
  if (!repository) {
    repository = new RepositoryClass.default();
  }

  return repository;
}



// WEBPACK FOOTER //
// ./src/app/repositories/Advert.ts